import { useEffect, useReducer } from 'react';
import * as moment from 'moment';
import { IListItem } from '../../../models';
import { Alert, Button, Col, Row } from 'react-bootstrap';
import { IValidation } from './validation';
import EntryContent from './entry-content';

interface IProps {
    firstName: string,
    lastName: string,
    birthDate: string,
    transplantDate: string,
    donationTypes: IListItem[],
    isUpdate: boolean,
    onCreateUpdate: (firstName: string, lastName: string, birthDate: string, transplantDate: string, donationType: number) => void,
    onCancel: () => void
}

interface IState {
    firstName: string,
    lastName: string,
    birthDate: string,
    donationType: number,
    transplantDate: string,
    validation: IValidation,
    isDirty: boolean
}

const initialState: IState = {
    firstName: '',
    lastName: '',
    birthDate: '',
    donationType: 0,
    transplantDate: '',
    validation: {
        firstName: true,
        lastName: true,
        birthDate: true,
        donationType: true,
        transplantDate: true
    },
    isDirty: false
};

const Actions = {
    SET_TRANSPLANT_DATE: 'SET_TRANSPLANT_DATE',
    SET_DONATION_TYPE: 'SET_DONATION_TYPE',
    SET_FIRST_NAME: 'SET_FIRST_NAME',
    SET_LAST_NAME: 'SET_LAST_NAME',
    SET_BIRTH_DATE: 'SET_BIRTH_DATE',
    SET_VALIDATION: 'SET_VALIDATION',
    SET_DIRTY: 'SET_DIRTY',
    INITIALIZE: 'INITIALIZE'
};

const reducer = (state: IState, action: { type: string, value: any }): IState => {
    switch (action.type) {
        case Actions.SET_FIRST_NAME:
            return { ...state, firstName: action.value };

        case Actions.SET_LAST_NAME:
            return { ...state, lastName: action.value };

        case Actions.SET_BIRTH_DATE:
            return { ...state, birthDate: action.value };

        case Actions.SET_DONATION_TYPE:
            return { ...state, donationType: action.value };

        case Actions.SET_TRANSPLANT_DATE:
            return { ...state, transplantDate: action.value };

        case Actions.SET_VALIDATION:
            let { validation, isDirty } = action.value;
            return { ...state, validation, isDirty };

        case Actions.INITIALIZE:
            let { firstName, lastName, birthDate, transplantDate } = action.value;

            return {
                ...state,
                firstName, lastName, birthDate, transplantDate
            };

        default:
            throw new Error();
    }
}

const AddUpdateEntry = (props: IProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        let { firstName, lastName, birthDate, transplantDate } = props;
        dispatch({ type: Actions.INITIALIZE, value: { firstName, lastName, birthDate, transplantDate } });
    }, [props.firstName, props.lastName, props.birthDate]);

    useEffect(() => {
        let { firstName, lastName, birthDate, donationType, transplantDate } = state.validation;
        let isValid = firstName && lastName && birthDate && donationType && transplantDate;

        if (state.isDirty && isValid) {
            let { firstName, lastName, birthDate, transplantDate, donationType } = state;
            props.onCreateUpdate(firstName, lastName, birthDate, transplantDate, donationType);
        }
    }, [state.validation, state.isDirty]);

    const validate = (): IValidation => {
        let { firstName, lastName, birthDate, donationType, transplantDate } = state;

        return {
            firstName: firstName != '',
            lastName: lastName != '',
            birthDate: birthDate != '' ? moment(birthDate, 'MM/DD/YYYY', true).isValid() : false,
            donationType: donationType > 0,
            transplantDate: transplantDate != '' ? moment(transplantDate, 'MM/DD/YYYY', true).isValid() : false,
        };
    };

    const handleFieldChange = (property: string, value: any) => {
        if (property == 'firstName') {
            dispatch({ type: Actions.SET_FIRST_NAME, value });
        }

        if (property == 'lastName') {
            dispatch({ type: Actions.SET_LAST_NAME, value });
        }

        if (property == 'birthDate') {
            dispatch({ type: Actions.SET_BIRTH_DATE, value });
        }

        if (property == 'donationType') {
            dispatch({ type: Actions.SET_DONATION_TYPE, value: parseInt(value) });
        }

        if (property == 'transplantDate') {
            dispatch({ type: Actions.SET_TRANSPLANT_DATE, value });
        }
    };

    const handleValidateBeforeCreate = () => {
        let validation = validate();

        dispatch({ type: Actions.SET_VALIDATION, value: { validation, isDirty: true } });
    };

    const { firstName, lastName, birthDate, donationType, transplantDate } = state.validation;
    const isValid = firstName && lastName && birthDate && donationType && transplantDate;

    return (
        <>
            <h4 style={{ marginBottom: 20 }}>{props.isUpdate ? 'Update Existing Entry' : 'Create New Entry'}</h4>

            {state.isDirty && !isValid && (
                <Alert bsStyle='danger' style={{ paddingTop: 7.5, paddingBottom: 7.5 }}>The items in red are required and must be specified</Alert>
            )}

            <EntryContent
                firstName={state.firstName}
                lastName={state.lastName}
                birthDate={state.birthDate}
                donationType={state.donationType}
                transplantDate={state.transplantDate}
                isUpdate={props.isUpdate}
                isDirty={state.isDirty}
                validation={state.validation}
                donationTypes={props.donationTypes}
                onUpdate={handleFieldChange}
            />

            <Row>
                <Col xs={12} lg={3} lgOffset={3}>
                    <Button bsStyle='primary' block onClick={handleValidateBeforeCreate} style={{ marginRight: 10 }}>
                        {props.isUpdate ? 'Update Existing Entry' : 'Create New Entry'}
                    </Button>
                </Col>
                <Col xs={12} lg={3}>
                    <Button block onClick={props.onCancel}>Cancel</Button>
                </Col>
            </Row>
        </>
    );
}

export default AddUpdateEntry;